import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, Pencil, Trash2, X, Globe, AlertTriangle, CheckCircle2,
  Link2, Eye, EyeOff, FileText, Sparkles, RotateCcw, Save, Loader2, BookOpen,
} from 'lucide-react';
import styles from './SeoPages.module.css';
import { useSeoPagesList, useSeoPageMutations } from '../../../api/cms/useSeoPagesApi';
import { useToast } from '../../../context/ToastContext';

/**
 * SEO Page Manager (SEO Master Phase 1 §6).
 *
 * THE CENTRAL IDEA, WHICH THE WHOLE SCREEN IS SHAPED AROUND: a row here is an OVERRIDE, not
 * the page. §4 requires the site to generate every eligible page automatically from existing
 * entity data, with "manual SEO input ... optional optimization, not required daily
 * maintenance". So the editor always shows what the page WOULD say on its own, and typing is
 * framed as overriding that rather than filling in a blank.
 *
 * Every derived value - the canonical URL, the automatic title, the warnings, whether the
 * page is in the sitemap - is computed by the backend and arrives with the row. The screen
 * renders them and never recomputes them, so it cannot preview a URL the website would not
 * actually serve.
 */

/** The public site, for the Google preview's breadcrumb. */
const SITE_ORIGIN = import.meta.env?.VITE_PUBLIC_SITE_URL || 'https://holidaybooking.be';

/* ────────────────────────── page types (§5) ────────────────────────── */

/**
 * The ten Phase 1 page types, each with the entity fields its URL is actually built from.
 *
 * `needs` drives the form: a Cheap Holiday page is a country page, so showing it a zone
 * picker invites someone to fill in a field that changes nothing. §5's URL architecture is
 * the source of this mapping.
 */
const PAGE_TYPES = [
  { value: 'COUNTRY', label: 'Country', example: '/zonvakanties/turkije', needs: ['holidayType', 'country'] },
  { value: 'DESTINATION', label: 'Destination', example: '/zonvakanties/turkije/antalya', needs: ['holidayType', 'country', 'destination'] },
  { value: 'ZONE', label: 'Zone', example: '/zonvakanties/turkije/antalya/side', needs: ['holidayType', 'country', 'destination', 'zone'] },
  { value: 'HOLIDAY_TYPE', label: 'Holiday type', example: '/zonvakanties', needs: ['holidayType'] },
  { value: 'HOTEL', label: 'Hotel', example: '/hotel/turkije/antalya/delphin-imperial', needs: ['country', 'destination', 'hotel', 'slug'] },
  { value: 'THEME', label: 'Theme', example: '/all-inclusive/turkije/antalya', needs: ['country', 'destination', 'theme'] },
  { value: 'LAST_MINUTE', label: 'Last minute', example: '/last-minute/turkije/antalya', needs: ['country', 'destination'] },
  { value: 'CHEAP_HOLIDAY', label: 'Cheap holiday', example: '/goedkope-vakanties/turkije', needs: ['country'] },
  { value: 'TRAVEL_GUIDE', label: 'Travel guide', example: '/reisgids/beste-reistijd-antalya', needs: ['slug'] },
  { value: 'CUSTOM_LANDING_PAGE', label: 'Campaign page', example: '/zomerdeals', needs: ['slug'] },
];

const typeLabel = (v) => PAGE_TYPES.find((t) => t.value === v)?.label || v;
const typeConfig = (v) => PAGE_TYPES.find((t) => t.value === v) || PAGE_TYPES[0];

/** Google truncates around these. Advice, never a gate (§6: "simple warnings are enough"). */
const TITLE_MAX = 60;
const DESC_MAX = 155;

const EMPTY_FORM = {
  internalName: '',
  pageType: 'COUNTRY',
  publicationStatus: 'DRAFT',
  indexStatus: 'INDEX',
  countryCode: '', destinationCode: '', zoneCode: '', hotelCode: '',
  holidayTypeId: '', themeCode: '', slug: '',
  guideCategory: '', primaryCommercialTargetId: '',
  seoTitle: '', metaDescription: '', h1: '',
  introContent: '', mainContent: '',
  heroImageUrl: '', heroImageAlt: '',
};

/* ══════════════════════════ list screen ══════════════════════════ */

export default function SeoPages() {
  const { showToast } = useToast();

  const [search, setSearch] = useState('');
  const [pageType, setPageType] = useState('');
  const [publicationStatus, setPublicationStatus] = useState('');

  const { data: rows, loading, error, refetch } = useSeoPagesList({
    search, pageType, publicationStatus,
  });

  const [editing, setEditing] = useState(null);   // row object, or {} for a new page
  const [confirmDelete, setConfirmDelete] = useState(null);

  const { remove, deleting } = useSeoPageMutations();

  const stats = useMemo(() => ({
    total: rows.length,
    published: rows.filter((r) => r.publicationStatus === 'PUBLISHED').length,
    draft: rows.filter((r) => r.publicationStatus === 'DRAFT').length,
    inSitemap: rows.filter((r) => r.inSitemap).length,
    warnings: rows.filter((r) => r.warnings?.length).length,
  }), [rows]);

  const doDelete = async () => {
    try {
      await remove(confirmDelete.id);
      showToast('SEO page deleted', 'success');
      setConfirmDelete(null);
      refetch();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to delete SEO page', 'error');
    }
  };

  const filtering = Boolean(search || pageType || publicationStatus);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>SEO Pages</h1>
          <p>
            Pages are generated automatically from your countries, destinations and hotels.
            Add a page here only to override what it says by default.
          </p>
        </div>
        <button className={styles.primaryBtn} onClick={() => setEditing({})}>
          <Plus size={15} /> New SEO page
        </button>
      </div>

      {error && <div className={styles.errorBox}>{error}</div>}

      <div className={styles.stats}>
        <StatTile
          value={stats.total} label="Override pages" icon={<FileText size={13} />}
          on={!filtering} onClick={() => { setSearch(''); setPageType(''); setPublicationStatus(''); }}
        />
        <StatTile
          value={stats.published} label="Published" icon={<CheckCircle2 size={13} />}
          on={publicationStatus === 'PUBLISHED'}
          onClick={() => setPublicationStatus(publicationStatus === 'PUBLISHED' ? '' : 'PUBLISHED')}
        />
        <StatTile
          value={stats.draft} label="Draft" icon={<Pencil size={13} />}
          on={publicationStatus === 'DRAFT'}
          onClick={() => setPublicationStatus(publicationStatus === 'DRAFT' ? '' : 'DRAFT')}
        />
        <StatTile value={stats.inSitemap} label="In sitemap" icon={<Globe size={13} />} />
        <StatTile
          value={stats.warnings} label="Need attention" icon={<AlertTriangle size={13} />} warn
        />
      </div>

      <div className={styles.filterBar}>
        <div className={styles.searchBar}>
          <Search size={15} className={styles.searchIcon} />
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Search by name, slug or title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search SEO pages"
          />
        </div>
        <select
          className={styles.select} value={pageType}
          onChange={(e) => setPageType(e.target.value)} aria-label="Filter by page type"
        >
          <option value="">All page types</option>
          {PAGE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select
          className={styles.select} value={publicationStatus}
          onChange={(e) => setPublicationStatus(e.target.value)} aria-label="Filter by status"
        >
          <option value="">Any status</option>
          <option value="PUBLISHED">Published</option>
          <option value="DRAFT">Draft</option>
          <option value="ARCHIVED">Archived</option>
        </select>
        <span className={styles.countLabel}>
          {loading ? 'Loading…' : `${rows.length} page${rows.length === 1 ? '' : 's'}`}
        </span>
      </div>

      <div className={styles.tableWrap}>
        {loading ? (
          <SkeletonTable />
        ) : rows.length === 0 ? (
          <EmptyState filtering={filtering} onCreate={() => setEditing({})} />
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Page</th>
                <th>Type</th>
                <th>Status</th>
                <th>Search</th>
                <th>Checks</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={styles.row} onClick={() => setEditing(r)}>
                  <td>
                    <div className={styles.pageName}>{r.internalName}</div>
                    {r.canonicalPath
                      ? <div className={styles.pageUrl}>{r.canonicalPath}</div>
                      : <div className={styles.pageUrlMissing}>No URL yet</div>}
                  </td>
                  <td><span className={styles.badge}>{typeLabel(r.pageType)}</span></td>
                  <td><StatusPill status={r.publicationStatus} /></td>
                  <td>
                    <span className={`${styles.pill} ${r.indexStatus === 'INDEX' ? styles.pillIndex : styles.pillNoindex}`}>
                      {r.indexStatus === 'INDEX' ? <Eye size={11} /> : <EyeOff size={11} />}
                      {r.indexStatus === 'INDEX' ? 'Indexed' : 'Noindex'}
                    </span>
                  </td>
                  <td>
                    {r.warnings?.length ? (
                      <span className={styles.warnChip}>
                        <AlertTriangle size={11} /> {r.warnings.length}
                      </span>
                    ) : <span className={styles.okChip}>All good</span>}
                  </td>
                  <td>
                    <div className={styles.rowActions}>
                      <button
                        className={styles.iconBtn} title="Edit"
                        onClick={(e) => { e.stopPropagation(); setEditing(r); }}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        className={`${styles.iconBtn} ${styles.iconBtnDanger}`} title="Delete"
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete(r); }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <SeoPageDrawer
          row={editing}
          allPages={rows}
          onClose={() => setEditing(null)}
          onSaved={(saved) => { refetch(); setEditing(saved); }}
        />
      )}

      {confirmDelete && (
        <>
          <div className={styles.overlay} onClick={() => setConfirmDelete(null)} />
          <div className={styles.confirmCard} role="dialog" aria-modal="true">
            <h3>Delete this SEO page?</h3>
            <p>
              “{confirmDelete.internalName}” will be removed. The underlying
              {' '}{typeLabel(confirmDelete.pageType).toLowerCase()} page stays live and goes back
              to its automatic title and description.
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.secondaryBtn} onClick={() => setConfirmDelete(null)}>
                Cancel
              </button>
              <button className={styles.dangerBtn} onClick={doDelete} disabled={deleting}>
                {deleting ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />} Delete
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ────────────────────────── small pieces ────────────────────────── */

function StatTile({ value, label, icon, on, warn, onClick }) {
  const cls = [styles.stat, on ? styles.statOn : '', warn ? styles.statWarn : '']
    .filter(Boolean).join(' ');
  return (
    <button type="button" className={cls} onClick={onClick} disabled={!onClick}>
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statLabel}>{icon} {label}</div>
    </button>
  );
}

function StatusPill({ status }) {
  const map = {
    PUBLISHED: [styles.pillPublished, 'Published'],
    DRAFT: [styles.pillDraft, 'Draft'],
    ARCHIVED: [styles.pillArchived, 'Archived'],
  };
  const [cls, text] = map[status] || map.DRAFT;
  return <span className={`${styles.pill} ${cls}`}>{text}</span>;
}

function SkeletonTable() {
  return (
    <div style={{ padding: 16 }}>
      {[...Array(5)].map((_, i) => (
        <div key={i} className={styles.skelRow} style={{ marginBottom: 14, width: `${90 - i * 7}%` }} />
      ))}
    </div>
  );
}

function EmptyState({ filtering, onCreate }) {
  return (
    <div className={styles.empty}>
      <Globe size={38} className={styles.emptyIcon} />
      <h3>{filtering ? 'No pages match those filters' : 'No overrides yet'}</h3>
      <p>
        {filtering
          ? 'Try clearing the search or choosing a different page type.'
          : 'Your country, destination and hotel pages already work with automatic titles and descriptions. Add an override here when you want to write your own for an important page.'}
      </p>
      {!filtering && (
        <button className={styles.primaryBtn} onClick={onCreate}>
          <Plus size={15} /> New SEO page
        </button>
      )}
    </div>
  );
}

/* ══════════════════════════ editor ══════════════════════════ */

function SeoPageDrawer({ row, onClose, onSaved, allPages = [] }) {
  const { showToast } = useToast();
  const { create, update, saving } = useSeoPageMutations();
  const isNew = !row?.id;

  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    ...Object.fromEntries(
      Object.keys(EMPTY_FORM).map((k) => [k, row?.[k] ?? EMPTY_FORM[k]]),
    ),
  }));

  // Reopened on a different row (or re-opened on the freshly created one), so reset.
  useEffect(() => {
    setForm({
      ...EMPTY_FORM,
      ...Object.fromEntries(
        Object.keys(EMPTY_FORM).map((k) => [k, row?.[k] ?? EMPTY_FORM[k]]),
      ),
    });
  }, [row?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = useCallback((k, v) => setForm((f) => ({ ...f, [k]: v })), []);

  // Close on Escape, which is what a slide-over is expected to do.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const cfg = typeConfig(form.pageType);
  const needs = (k) => cfg.needs.includes(k);

  /**
   * The automatic values come from the server and depend on the entity, not on what is being
   * typed, so they stay valid while the author edits. A brand new page has none yet: it has
   * no entity resolved server-side until the first save, which is why creating keeps the
   * drawer open on the saved row rather than closing it.
   */
  const autoTitle = row?.autoSeoTitle || null;
  const autoH1 = row?.autoH1 || null;

  const effTitle = form.seoTitle?.trim() || autoTitle || '';
  const effH1 = form.h1?.trim() || autoH1 || '';
  const effDesc = form.metaDescription?.trim() || '';

  const save = async () => {
    if (!form.internalName.trim()) {
      showToast('Give the page an internal name first', 'error');
      return;
    }
    // Empty strings are sent as null so the backend stores "use the automatic value" rather
    // than "deliberately blank" (§4). The two are different and must stay different.
    const payload = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, typeof v === 'string' && v.trim() === '' ? null : v]),
    );
    payload.internalName = form.internalName.trim();
    payload.holidayTypeId = form.holidayTypeId ? Number(form.holidayTypeId) : null;

    try {
      const saved = isNew ? await create(payload) : await update(row.id, payload);
      showToast(isNew ? 'SEO page created' : 'SEO page saved', 'success');
      onSaved(saved);
    } catch (err) {
      showToast(err?.response?.data?.message || 'Could not save the SEO page', 'error');
    }
  };

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <aside className={styles.drawer} role="dialog" aria-modal="true" aria-label="Edit SEO page">
        <div className={styles.drawerHead}>
          <div>
            <h2>{isNew ? 'New SEO page' : form.internalName || 'SEO page'}</h2>
            <p>{isNew ? 'Overrides what this page says by default' : typeLabel(form.pageType)}</p>
          </div>
          <button className={styles.iconBtn} onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className={styles.drawerBody}>
          {/* The Google preview, first, because it is what the author is actually deciding. */}
          <SerpPreview
            path={row?.canonicalPath}
            title={effTitle}
            description={effDesc}
            noindex={form.indexStatus === 'NOINDEX'}
          />

          {!isNew && row?.warnings?.length > 0 && (
            <div className={styles.warnPanel}>
              <h4><AlertTriangle size={13} /> {row.warnings.length} thing{row.warnings.length === 1 ? '' : 's'} to look at</h4>
              <ul>{row.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
              <p className={styles.warnFootnote}>
                These are suggestions. Nothing here stops you publishing.
              </p>
            </div>
          )}

          {/* ── identity ── */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}><Link2 size={14} /> Which page is this?</h3>
            <p className={styles.sectionHint}>
              The page type decides the URL. {cfg.example}
            </p>

            <div className={styles.grid2}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="sp-name">
                  Internal name <span className={styles.req}>*</span>
                </label>
                <input
                  id="sp-name" className={styles.input} value={form.internalName}
                  onChange={(e) => set('internalName', e.target.value)}
                  placeholder="Turkey — sun holidays"
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="sp-type">Page type</label>
                <select
                  id="sp-type" className={styles.select} style={{ width: '100%' }}
                  value={form.pageType} onChange={(e) => set('pageType', e.target.value)}
                >
                  {PAGE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Only the entity fields this page type's URL is built from. */}
            <div className={styles.grid2}>
              {needs('holidayType') && (
                <Field label="Holiday type ID" value={form.holidayTypeId}
                  onChange={(v) => set('holidayTypeId', v)} placeholder="e.g. 3" mono />
              )}
              {needs('country') && (
                <Field label="Country code" value={form.countryCode}
                  onChange={(v) => set('countryCode', v)} placeholder="TR" mono />
              )}
              {needs('destination') && (
                <Field label="Destination code" value={form.destinationCode}
                  onChange={(v) => set('destinationCode', v)} placeholder="AYT" mono />
              )}
              {needs('zone') && (
                <Field label="Zone code" value={form.zoneCode}
                  onChange={(v) => set('zoneCode', v)} placeholder="AYT:SIDE" mono />
              )}
              {needs('hotel') && (
                <Field label="Hotelbeds hotel code" value={form.hotelCode}
                  onChange={(v) => set('hotelCode', v)} placeholder="123456" mono />
              )}
              {needs('theme') && (
                <Field label="Theme / board code" value={form.themeCode}
                  onChange={(v) => set('themeCode', v)} placeholder="AI" mono />
              )}
              {needs('slug') && (
                <Field label="Slug" value={form.slug}
                  onChange={(v) => set('slug', v)} placeholder="beste-reistijd-antalya" mono />
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Canonical URL</label>
              <div className={styles.urlPreview}>
                {row?.canonicalPath
                  ? <><Globe size={13} />{SITE_ORIGIN}{row.canonicalPath}</>
                  : <span className={styles.urlPreviewEmpty}>
                      {isNew
                        ? 'Appears once you save and the entity is resolved.'
                        : 'Not routable yet: fill in the fields above.'}
                    </span>}
              </div>
            </div>
          </div>

          {/* ── search appearance ── */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}><Search size={14} /> Search appearance</h3>
            <p className={styles.sectionHint}>
              Leave a box empty to use the automatic value. Anything you type here replaces it
              and is never overwritten by a content refresh.
            </p>

            <OverrideField
              id="sp-title" label="SEO title" value={form.seoTitle}
              onChange={(v) => set('seoTitle', v)} auto={autoTitle}
              max={TITLE_MAX} effective={effTitle} isNew={isNew}
            />
            <OverrideField
              id="sp-desc" label="Meta description" value={form.metaDescription}
              onChange={(v) => set('metaDescription', v)} auto={null}
              max={DESC_MAX} effective={effDesc} textarea isNew={isNew}
              hint="No automatic description exists. Without one, Google writes its own from the page text."
            />
            <OverrideField
              id="sp-h1" label="Page heading (H1)" value={form.h1}
              onChange={(v) => set('h1', v)} auto={autoH1}
              effective={effH1} isNew={isNew}
            />
          </div>

          {/* ── Travel Guide only (§14) ── */}
          {form.pageType === 'TRAVEL_GUIDE' && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}><BookOpen size={14} /> Article</h3>
              <p className={styles.sectionHint}>
                An article has nothing behind it but what you write, so it stays a 404 until
                you publish it, and it needs its own heading and description.
              </p>

              <div className={styles.grid2}>
                <Field
                  label="Category" value={form.guideCategory}
                  onChange={(v) => set('guideCategory', v)}
                  placeholder="Beste reistijd"
                />
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="sp-target">Links to</label>
                  <select
                    id="sp-target" className={styles.select} style={{ width: '100%' }}
                    value={form.primaryCommercialTargetId || ''}
                    onChange={(e) => set('primaryCommercialTargetId', e.target.value)}
                  >
                    <option value="">No link</option>
                    {/* Published pages only: an article pointing at a draft would render a
                        link to a 404, so the resolver drops it anyway. */}
                    {allPages
                      .filter((p) => p.pageType !== 'TRAVEL_GUIDE'
                        && p.publicationStatus === 'PUBLISHED'
                        && p.id !== row?.id)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.internalName} — {typeLabel(p.pageType)}
                        </option>
                      ))}
                  </select>
                  <div className={styles.autoNote}>
                    Where the article sends a reader who wants to book.
                  </div>
                </div>
              </div>

              {/* Tagging a place is what makes the article show up on that place's page. */}
              <div className={styles.grid2}>
                <Field label="About country (code)" value={form.countryCode}
                  onChange={(v) => set('countryCode', v)} placeholder="TR" mono />
                <Field label="About destination (code)" value={form.destinationCode}
                  onChange={(v) => set('destinationCode', v)} placeholder="AYT" mono />
              </div>
              <div className={styles.autoNote}>
                Tag a place and this article appears in the related-reading block on that
                page. A destination-tagged article also shows on its country page.
              </div>
            </div>
          )}

          {/* ── content ── */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}><FileText size={14} /> Content</h3>
            <p className={styles.sectionHint}>
              Optional. Shown on the page itself, above and below the commercial blocks.
            </p>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="sp-intro">Intro</label>
              <textarea
                id="sp-intro" className={styles.textarea} rows={3} value={form.introContent}
                onChange={(e) => set('introContent', e.target.value)}
                placeholder="A short paragraph under the heading…"
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="sp-main">Main content</label>
              <textarea
                id="sp-main" className={styles.textarea} rows={7} value={form.mainContent}
                onChange={(e) => set('mainContent', e.target.value)}
                placeholder="The body of the page…"
              />
            </div>
            <div className={styles.grid2}>
              <Field label="Hero image URL" value={form.heroImageUrl}
                onChange={(v) => set('heroImageUrl', v)} placeholder="https://…" />
              <Field label="Hero image alt text" value={form.heroImageAlt}
                onChange={(v) => set('heroImageAlt', v)} placeholder="Describe the image" />
            </div>
          </div>

          {/* ── publication ── */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}><Globe size={14} /> Publication</h3>
            <p className={styles.sectionHint}>
              A page reaches the sitemap only when it is Published and set to Indexed.
            </p>
            <div className={styles.grid2}>
              <div className={styles.field}>
                <label className={styles.label}>Status</label>
                <div className={styles.segmented}>
                  {['DRAFT', 'PUBLISHED', 'ARCHIVED'].map((s) => (
                    <button
                      key={s} type="button"
                      className={`${styles.segment} ${form.publicationStatus === s ? styles.segmentOn : ''}`}
                      onClick={() => set('publicationStatus', s)}
                    >
                      {s.charAt(0) + s.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Search engines</label>
                <div className={styles.segmented}>
                  {[['INDEX', 'Indexed'], ['NOINDEX', 'Hidden']].map(([s, lbl]) => (
                    <button
                      key={s} type="button"
                      className={`${styles.segment} ${form.indexStatus === s ? styles.segmentOn : ''}`}
                      onClick={() => set('indexStatus', s)}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.drawerFoot}>
          <span className={styles.countLabel}>
            {!isNew && row?.inSitemap
              ? <><CheckCircle2 size={12} /> In the sitemap</>
              : !isNew ? 'Not in the sitemap' : ''}
          </span>
          <div style={{ display: 'flex', gap: 9 }}>
            <button className={styles.secondaryBtn} onClick={onClose}>Cancel</button>
            <button className={styles.primaryBtn} onClick={save} disabled={saving}>
              {saving ? <Loader2 size={14} /> : <Save size={14} />}
              {isNew ? 'Create page' : 'Save changes'}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

/* ────────────────────────── form primitives ────────────────────────── */

function Field({ label, value, onChange, placeholder, mono }) {
  return (
    <div className={styles.field}>
      <label className={styles.label}>{label}</label>
      <input
        className={`${styles.input} ${mono ? styles.mono : ''}`}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

/**
 * A field that is an override of an automatic value (§4).
 *
 * The automatic value is shown, not hidden: the author can see what they are replacing and
 * get back to it in one click. That is the difference between "fill in this blank" and
 * "override this if you want to", which is what §4 asks the whole manager to be.
 */
function OverrideField({
  id, label, value, onChange, auto, max, effective, textarea, hint, isNew,
}) {
  const typed = (value ?? '').trim();
  const usingAuto = !typed && Boolean(auto);
  const len = (effective || '').length;
  const over = max != null && len > max;

  const Input = textarea ? 'textarea' : 'input';

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          {label}
          {usingAuto && <span className={styles.usingAuto}><Sparkles size={9} /> AUTO</span>}
          {typed && auto && <span className={styles.overridden}>OVERRIDDEN</span>}
        </span>
        {max != null && (
          <span className={`${styles.counter} ${over ? styles.counterOver : ''}`}>
            {len}/{max}
          </span>
        )}
      </label>

      <Input
        id={id}
        className={`${styles.input} ${textarea ? styles.textarea : ''}`}
        rows={textarea ? 3 : undefined}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={auto || (isNew ? 'Automatic value appears after saving' : 'Write your own…')}
      />

      {auto && (
        <div className={styles.autoNote}>
          <Sparkles size={11} />
          Automatic: <span className={styles.autoValue}>{auto}</span>
          {typed && (
            <>
              {' · '}
              <button type="button" className={styles.resetLink} onClick={() => onChange('')}>
                <RotateCcw size={9} /> use automatic
              </button>
            </>
          )}
        </div>
      )}
      {!auto && hint && <div className={styles.autoNote}>{hint}</div>}
      {over && (
        <div className={styles.autoNote} style={{ color: '#b45309' }}>
          Google usually cuts this off around {max} characters.
        </div>
      )}
    </div>
  );
}

/**
 * What the page looks like in Google.
 *
 * The single most useful thing an SEO screen can show: the author is writing a search result,
 * not a database row, and a title that reads well in a form often reads badly in a SERP. The
 * truncation here mirrors Google's own behaviour rather than hard-limiting the field, because
 * §6 says warnings, not gates.
 */
function SerpPreview({ path, title, description, noindex }) {
  const shownTitle = title
    ? (title.length > TITLE_MAX ? `${title.slice(0, TITLE_MAX - 1).trimEnd()}…` : title)
    : null;
  const shownDesc = description
    ? (description.length > DESC_MAX ? `${description.slice(0, DESC_MAX - 1).trimEnd()}…` : description)
    : null;

  const crumbs = (path || '').split('/').filter(Boolean);

  return (
    <div className={styles.serp}>
      <div className={styles.serpLabel}>
        <Search size={11} /> How this looks in Google
      </div>

      {noindex && (
        <div className={styles.autoNote} style={{ marginBottom: 10, color: '#b91c1c' }}>
          <EyeOff size={11} /> Set to Hidden, so it will not appear in search results at all.
        </div>
      )}

      <div className={styles.serpCard}>
        <div className={styles.serpCrumb}>
          <span className={styles.serpSite}>{SITE_ORIGIN.replace(/^https?:\/\//, '')}</span>
          {crumbs.length > 0 && <span className={styles.serpPath}>› {crumbs.join(' › ')}</span>}
        </div>

        <div className={styles.serpTitle}>
          {shownTitle || <span className={styles.serpMuted}>No title yet</span>}
        </div>

        <div className={styles.serpDesc}>
          {shownDesc || (
            <span className={styles.serpMuted}>
              No description. Google will pick a sentence from the page instead.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
